import { makeStyles } from '@mui/styles';
import Loader from 'apollo-react/components/Loader';
import Modal from 'apollo-react/components/Modal';
import Tab from 'apollo-react/components/Tab';
import Tabs from 'apollo-react/components/Tabs';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addDistributionListRows,
  deleteDistributionListRow,
  revertDistributionToOriginal,
  saveRepositoryDistributionConfig,
  setDistributionModalOpen,
  setRepositoryPropValue,
  validateRepository,
} from '../../actions/RepositoryConfigurationActions';
import { clearWarnings } from '../../actions/WarningActions';
import DistributionTab from '../../components/DistributionTab';
import {
  ADMIN_DISTRIBUTION_FOR_DETAILS,
  DISTRIBUTION_FOR_DETAILS,
} from '../../constants/SmartInsightConstants';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { equalityFn } from '../../form-designer/utils/form-manager.utils';
import { getModifiedDistributionRows } from '../../saved-search-designer/utils/common.utils';
import {
  getDistributionFor,
  getDistributionTableRows,
  getIsDistributionModalOpen,
  hasDistributionUnsavedChanges,
} from '../../selectors/RepositoryConfigurationSelectors';

const useStyles = makeStyles({
  modal: {
    minWidth: '70%',
    minHeight: '50%',
    '& [class*="MuiDialogContent-root"]': {
      paddingTop: 10,
    },
  },
  tabContent: {
    marginTop: 25,
  },
});

const Distribution = ({ distributionType, isAdmin }) => {
  const dispatch = useDispatch();

  const distributionFor = useSelector((state) => getDistributionFor(state, { distributionType }));
  const distributionList = useSelector(
    (state) =>
      getDistributionTableRows(state, { tablePath: `${distributionType}.distributionList` }),
    equalityFn
  );

  const onChangeDistributionValue = ({ target: { value } }) => {
    if (distributionFor === value) {
      return;
    }

    dispatch(setRepositoryPropValue({ prop: `${distributionType}.distributionFor`, value }));
    dispatch(setRepositoryPropValue({ prop: `${distributionType}.distributionList`, value: [] }));
    dispatch(validateRepository());
  };

  const handleZoomSelectViewDistributionList = useCallback(
    ({ row, selection = [] }) => {
      if (selection.length) {
        const savedSearchDetails = isAdmin
          ? ADMIN_DISTRIBUTION_FOR_DETAILS[row.distributionFor]
          : DISTRIBUTION_FOR_DETAILS[distributionFor];

        dispatch(deleteDistributionListRow({ rowId: row.rowId, distributionType }));

        const rows = getModifiedDistributionRows({
          rows: selection,
          mappingFields: savedSearchDetails.mappingFields,
          distributionFor,
          isAdmin,
        });

        dispatch(addDistributionListRows({ newRows: rows, distributionType }));
        dispatch(validateRepository());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [distributionFor]
  );

  const handleAddNewDistributionRow = ({ newRow }) =>
    dispatch(addDistributionListRows({ newRows: [newRow], distributionType }));
  const handleDeleteNewDistributionRow = ({ rowId }) =>
    dispatch(deleteDistributionListRow({ rowId, distributionType }));
  const handleValidateDistribution = () => dispatch(validateRepository());

  return (
    <DistributionTab
      distributionForValueStore={distributionFor}
      distributionListRows={distributionList}
      handleAddNewDistributionRow={handleAddNewDistributionRow}
      onChangeDistributionValue={onChangeDistributionValue}
      handleDeleteDistributionRow={handleDeleteNewDistributionRow}
      handleZoomSelectDistributionList={handleZoomSelectViewDistributionList}
      validateDistribution={handleValidateDistribution}
      isAdmin={isAdmin}
      fullWidth
    />
  );
};

const DistributionModal = () => {
  const translateString = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [selectedTab, setSelectedTab] = useState(0);

  const isOpen = useSelector(getIsDistributionModalOpen);
  const [isLoading, setIsLoading] = useState(false);
  const hasUnsavedChanges = useSelector(hasDistributionUnsavedChanges);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTab(0);
    }
  }, [isOpen]);

  const handleChangeTab = (e, value) => {
    setSelectedTab(value);
  };

  const handleClose = () => {
    dispatch(setDistributionModalOpen(false));

    if (hasUnsavedChanges) {
      dispatch(revertDistributionToOriginal);
      dispatch(clearWarnings());
    }
  };

  return (
    <>
      <Modal
        open={isOpen}
        variant="default"
        onClose={handleClose}
        title={translateString('Distribution List')}
        buttonProps={[
          {
            label: translateString('Cancel'),
            onClick: handleClose,
          },
          {
            label: translateString('Save'),
            onClick: () => dispatch(saveRepositoryDistributionConfig(setIsLoading)),
            variant: 'primary',
            disabled: !hasUnsavedChanges,
          },
        ]}
        className={classes.modal}
        disableBackdropClick
        disableEscapeKeyDown
      >
        <Tabs value={selectedTab} onChange={handleChangeTab} truncate>
          <Tab label={translateString('Viewer Distribution List')} />
          <Tab label={translateString('Admin Distribution List')} />
        </Tabs>
        <div className={classes.tabContent}>
          {selectedTab === 0 && <Distribution distributionType="viewDistribution" />}
          {selectedTab === 1 && <Distribution distributionType="adminDistribution" isAdmin />}
        </div>
      </Modal>
      {isLoading && <Loader isInner />}
    </>
  );
};

export default DistributionModal;
